import React, { useCallback, useEffect, useState } from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import { BriefIcon } from '@fiverr-private/icons';
import { BriefDrawer } from '@fiverr-private/ai_brief';
import { useBusinessSignInSteps } from '@fiverr-private/identification_triggers';
import { getContext } from '@fiverr-private/fiverr_context';
import { BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { Container } from '@fiverr-private/layout_components';
import { useFreelancerPageContext } from '../../../../FreelancerPageContext';
import { mixpanel, PAGE_NAME, SOURCES, useMonitoring } from '../../../../../monitoring';
import { useRelevantGigs } from '../../../../../hooks/useRelevantGigs';
import { handleSignUpModalOpen } from '../../../../../utils/getGlobals';
import { ContactOptionRow } from './ContactOptionRow';

const T_PREFIX = 'freelancer_page.contact.dropdown';
const COMPONENT_NAME = 'contact_me_dropdown';

export const AiBriefMenuItem = ({ parentComponentOpen, onClick = noop, options, onContactClick = noop, trigger }) => {
    const [isShowDrawer, setIsShowDrawer] = useState(false);
    const [isDrawerLoading, setIsDrawerLoading] = useState(false);
    const { sellerId, seller, gigs, isLoggedIn } = useFreelancerPageContext();

    const sellerUsername = seller?.user?.name;
    const sellerDisplayName = seller?.user?.profile?.displayName || sellerUsername;
    const responseTime = seller?.responseTime?.inHours;
    const { handleClickContactButtonEvent } = useMonitoring();

    const { Providers, fireImpressionEvents } = useMonitoring();

    const mostRelevantGig = useRelevantGigs(gigs)?.[0];
    const { userId, organizationId } = getContext();

    const handleContactClick = () => {
        closeDrawer();
        onContactClick();
        handleClickContactButtonEvent({
            source: options.source,
            componentName: 'productized brief',
            page: {
                component: {
                    name: 'productized brief',
                },
            },
        });
    };

    const sendBiImpressionEvent = useCallback(() => {
        fireImpressionEvents({
            [Providers.BigQuery]: {
                type: 'professional_brief_button_shown',
                group: 'user_impression',
                sub_group: 'fiverr_pro',
                user: {
                    id: userId,
                },
                seller: {
                    id: Number(sellerId),
                },
                organization: {
                    id: organizationId,
                },
                action: {
                    type: 'imp',
                },
                page: {
                    name: PAGE_NAME,
                    element: {
                        name: 'button',
                    },
                },
                feature: {
                    name: 'fiverr_pro',
                },
                gig: {
                    id: mostRelevantGig?.gig_id,
                },
                buyer: {
                    request: {
                        category_id: mostRelevantGig.category_id,
                        subcategory_id: mostRelevantGig.sub_category_id,
                    },
                },
                out_of_session: 'False',
            },
        });
    }, [Providers.BigQuery, fireImpressionEvents, mostRelevantGig, organizationId, sellerId, userId]);

    useEffect(() => {
        if (parentComponentOpen) {
            sendBiImpressionEvent();
        }
    }, [sendBiImpressionEvent, parentComponentOpen]);

    const openDrawer = () => {
        if (!isLoggedIn) {
            return handleSignUpModalOpen({
                source: options?.source,
                type: BUSINESS_TRIGGER_CTA.CONTACT,
                data: { sellerName: sellerDisplayName },
            });
        }

        setIsDrawerLoading(true);
        setIsShowDrawer(true);
    };

    const { signInSteps } = useBusinessSignInSteps({
        id: `brief-drawer-${sellerId}`,
        onSuccess: openDrawer,
        signInModalParams: {
            source: PAGE_NAME,
            businessTriggerCtaType: BUSINESS_TRIGGER_CTA.NO_RESULT_PAGE_BRIEF,
        },
        migrateModalParams: {
            source: PAGE_NAME,
            biEnrichment: {
                pageElementName: SOURCES.page,
                trigger: BUSINESS_TRIGGER_CTA.NO_RESULT_PAGE_BRIEF,
            },
        },
    });

    const closeDrawer = () => {
        setIsShowDrawer(false);
        setIsDrawerLoading(false);
    };

    const handleClick = () => {
        signInSteps();
        onClick();
        mixpanel.clickOnContactMeOption({ ...options, option: 'Create a brief' });
    };

    return (
        <>
            <Container onClick={handleClick} width="inherit">
                {trigger || <ContactOptionRow renderIcon={BriefIcon} translationRoute={`${T_PREFIX}.create_brief`} />}
            </Container>
            <BriefDrawer
                componentName={COMPONENT_NAME}
                source={PAGE_NAME}
                seller={{
                    id: Number(sellerId),
                    userName: sellerUsername,
                    displayName: sellerDisplayName,
                    responseTime,
                }}
                initialText={options?.aiBriefInitialGenerationText}
                shouldRefreshData={isShowDrawer}
                categoryId={mostRelevantGig?.category_id}
                subCategoryId={mostRelevantGig?.sub_category_id}
                isOpen={isShowDrawer && !isDrawerLoading}
                onClose={closeDrawer}
                onLoadingChange={setIsDrawerLoading}
                onContactSellerClick={handleContactClick}
            />
        </>
    );
};

AiBriefMenuItem.propTypes = {
    parentComponentOpen: PropTypes.bool,
    onClick: PropTypes.func,
    options: PropTypes.shape({
        priorInteraction: PropTypes.bool,
        source: PropTypes.string,
        aiBriefInitialGenerationText: PropTypes.string,
    }),
    trigger: PropTypes.element,
};
